<template lang="html">
  <cw-service-request
    ref="serviceRequest"
    serviceName="Set"
    serviceIcon=""
    @submitted="submit"
  >
    <div slot="other-cells" class="">
      <cw-service-data-cell
        :description="projectName.description"
        :inputValue="projectName.inputValue"
        :stepNumber="projectName.stepNumber"
        :editing.sync="projectName.editing"
        expandedHeight="30vh"
        overflowType="scroll"
        :class="{ danger: $v.project.$dirty && !$v.project.required }"
      >
        <div slot="input-control">
          <cw-project-list
            :projects="currentProjects"
            :selectionHandler="projectSelected"
            :selectedProjectId="selectedProjectId"
            :showDisclosureIndicators="false"
          >
          </cw-project-list>
        </div>
      </cw-service-data-cell>
      <cw-service-data-cell
        :description="lotNumber.description"
        :inputValue="lotNumber.inputValue"
        :stepNumber="lotNumber.stepNumber"
        :editing.sync="lotNumber.editing"
        expandedHeight="30vh"
        overflowType="scroll"
        :class="{
          danger:
            $v.selectedLotId &&
            $v.selectedLotId.$dirty &&
            !$v.selectedLotId.required,
        }"
      >
        <div slot="input-control" class="control">
          <input
            class="input is-underlined"
            v-model="lotFilter"
            type="text"
            placeholder="Enter lot number"
          />
          <p
            class="help has-text-primary has-text-centered"
            v-if="filteredLots && !filteredLots.length"
          >
            If there are no available lots, you cannot set a box.<br />Contact
            Dispatch to create lots for this project.
          </p>
          <ul>
            <li v-for="(lot, index) in filteredLots" :key="index">
              <cw-list-cell
                :title="`Lot ${lot.name}`"
                :showDisclosureIndicator="false"
                :showSelectedIndicator="lot.subProjectId === selectedLotId"
                class="has-border-bottom"
                @click="lotSelected(lot)"
              >
              </cw-list-cell>
            </li>
          </ul>
        </div>
      </cw-service-data-cell>
      <cw-service-data-cell
        :description="intendedUse.description"
        :inputValue="intendedUse.inputValue"
        :displayValue="selectedUseDisplay"
        :stepNumber="intendedUse.stepNumber"
        :editing.sync="intendedUse.editing"
        :class="{ danger: $v.use.$dirty && !$v.use.required }"
      >
        <div slot="input-control" class="control">
          <cw-dropdown
            class="selection-dropdown anchored-bottom"
            :isActive.sync="intendedUse.editing"
          >
            <div slot="trigger">
              <p>{{ selectedUseDisplay || 'Choose a category' }}</p>
            </div>
            <!-- duplicate dropdown in PullRequest.vue -->
            <ul v-for="use in boxUses" :key="use.value">
              <li class="dropdown-item" @click="intendedUseSelected(use.value)">
                {{ use.display }}
              </li>
            </ul>
          </cw-dropdown>
        </div>
      </cw-service-data-cell>
      <cw-service-data-cell
        :description="boxSize.description"
        :inputValue="boxSize.inputValue"
        :displayValue="selectedSizeDisplay"
        :stepNumber="boxSize.stepNumber"
        :editing.sync="boxSize.editing"
        :class="{ danger: $v.size.$dirty && !$v.size.required }"
      >
        <div slot="input-control" class="control">
          <cw-dropdown
            class="selection-dropdown anchored-bottom"
            :isActive.sync="boxSize.editing"
          >
            <div slot="trigger">
              <p>{{ selectedSizeDisplay || 'Choose a size' }}</p>
            </div>
            <ul v-for="size in boxSizes" :key="size.value">
              <li class="dropdown-item" @click="boxSizeSelected(size.value)">
                {{ size.display }}
              </li>
            </ul>
          </cw-dropdown>
        </div>
      </cw-service-data-cell>
      <cw-service-data-cell
        v-if="showPOs"
        :description="purchaseOrder.description"
        :inputValue="purchaseOrder.inputValue"
        :stepNumber="purchaseOrder.stepNumber"
        :editing.sync="purchaseOrder.editing"
        :expandedHeight="availablePOs.length > 0 ? '30vh' : undefined"
        overflowType="scroll"
      >
        <div slot="input-control">
          <input
            class="input is-underlined"
            type="text"
            placeholder="Enter Purchase Order Number"
            v-model="purchaseOrder.inputValue"
          />
          <p class="help">Optional but required before pull.</p>
          <ul>
            <li v-for="(po, index) in purchaseOrders" :key="index">
              <cw-list-cell
                :title="po.purchaseOrderNumber"
                :showDisclosureIndicator="false"
                :showSelectedIndicator="po.purchaseOrderNumber === selectedPO"
                class="has-border-bottom"
                @click="poSelected(po)"
              >
              </cw-list-cell>
            </li>
          </ul>
        </div>
      </cw-service-data-cell>

      <!-- <div class="modal" :class="{'is-active': projectName.editing}">
        <div class="modal-background" @click="hideProjectList"></div>
        <div class="modal-content">
          <cw-project-list
            :projects="projects"
            :selectionHandler="projectSelected">
          </cw-project-list>
        </div>
        <button class="modal-close is-large" aria-label="close" @click="hideProjectList"></button>
      </div> -->
    </div>
  </cw-service-request>
</template>

<script>
import {
  PROJECTS_QUERY,
  PROJECTS_FOR_LOCATION_QUERY,
  PROJECT_SUBPROJECTS_QUERY,
  AVAILABLE_POS,
  BOX_OPTIONS,
  LAST_SET_SETTINGS,
  CREATE_DISPATCH_SET,
} from '@/graphql/constants';
import authRequired from '@/components/mixins/auth-required';
import { validationMixin } from 'vuelidate';
import { mapState, mapGetters } from 'vuex';

import { required } from 'vuelidate/lib/validators';

export default {
  name: 'set-request',
  mixins: [authRequired, validationMixin],
  components: {},
  props: {},
  data() {
    return {
      projects: [],
      projectsForLocation: [],
      projectName: {
        description: 'Project Name',
        inputValue: '',
        stepNumber: 3,
        editing: false,
      },
      boxOptions: {},
      lastSetSettings: null,
      lastSetSettingsApplied: false,
      selectedProjectId: this.$route.params.id || '',
      lots: [],
      selectedLotId: '',
      availablePOs: [],
      selectedPO: '',
      lotFilter: '',
      lotNumber: {
        description: 'Lot No.',
        inputValue: '',
        stepNumber: 4,
        editing: false,
      },
      intendedUse: {
        description: 'Intended Use',
        inputValue: '',
        stepNumber: 5,
        editing: false,
      },
      boxSize: {
        description: 'Box Size',
        inputValue: '',
        stepNumber: 6,
        editing: false,
      },
      purchaseOrder: {
        description: 'Purchase Order',
        inputValue: '',
        stepNumber: 7,
        editing: false,
      },
    };
  },
  validations() {
    let base = {
      project: {
        required,
      },
      use: {
        required,
      },
      size: {
        required,
      },
    };

    if (this.lots && this.lots.length > 1) {
      base.selectedLotId = {
        required,
      };
    }

    return base;
  },
  apollo: {
    projects: {
      query: PROJECTS_QUERY,
      update: data => data.projects || [],
      result({ data }) {
        // Only auto-select project if we're using regular projects (not location-specific)
        if (!this.hasMultipleLocations || !this.selectedCustomerId) {
          let projectId = this.selectedProjectId;
          let project = (data.projects || []).find(e => {
            return e.projectId === projectId;
          });
          this.projectSelected(project);
        }
      },
      skip() {
        // Skip regular projects query if we're using location-specific projects
        return this.hasMultipleLocations && Boolean(this.selectedCustomerId);
      },
    },
    projectsForLocation: {
      query: PROJECTS_FOR_LOCATION_QUERY,
      variables() {
        return {
          customerId: this.selectedCustomerId,
        };
      },
      update: data => data.projectsForLocation || [],
      result({ data }) {
        // Auto-select project when using location-specific projects
        let projectId = this.selectedProjectId;
        let project = (data.projectsForLocation || []).find(e => {
          return e.projectId === projectId;
        });
        this.projectSelected(project);
      },
      skip() {
        // Only skip if we don't have multiple locations or no customer ID selected
        return !this.hasMultipleLocations || !this.selectedCustomerId;
      },
    },
    lots: {
      query: PROJECT_SUBPROJECTS_QUERY,
      variables() {
        return { projectId: this.selectedProjectId };
      },
      update(data) {
        return data.subProjects;
      },
      skip() {
        return !this.selectedProjectId || this.selectedProjectId.length < 1;
      },
    },
    availablePOs: {
      query: AVAILABLE_POS,
      variables() {
        return { subProjectId: this.selectedLotId };
      },
      update: data => data.purchaseOrdersAvailable,
      skip() {
        return (
          !this.showPOs || !this.selectedLotId || this.selectedLotId.length < 1
        );
      },
    },
    boxOptions: {
      query: BOX_OPTIONS,
      update: data => data && data.boxOptions,
    },
  },
  computed: {
    ...mapState('locations', ['selectedCustomerId']),
    ...mapGetters('locations', ['hasMultipleLocations']),

    currentProjects() {
      // Use location-specific projects if available, otherwise fall back to regular projects
      const useLocationSpecific =
        this.hasMultipleLocations && Boolean(this.selectedCustomerId);

      if (useLocationSpecific && this.projectsForLocation) {
        return this.projectsForLocation.filter(p => !p.archived);
      }

      if (this.projects) {
        return this.projects.filter(p => !p.archived);
      }

      return [];
    },
    project() {
      return this.projectName.inputValue;
    },
    instructions() {
      return this.$refs.serviceRequest.specialInstructions.inputValue;
    },
    use() {
      return this.intendedUse.inputValue;
    },
    size() {
      return this.boxSize.inputValue;
    },
    filteredLots() {
      let lots = this.lots;
      let query = this.lotFilter.toLowerCase().replace(/lot /g, '');

      if (query.length > 0) {
        lots = lots.filter(lot => {
          return lot.name.toLowerCase().indexOf(query) > -1;
        });
      }
      return lots;
    },
    showPOs() {
      return (
        this.$store.state.user.customer &&
        this.$store.state.user.customer.requiresPO
      );
    },
    regularPOs() {
      return (
        (this.availablePOs && this.availablePOs.filter(po => !po.isRespotPO)) ||
        []
      );
    },
    purchaseOrders() {
      return this.regularPOs;
    },
    boxUses() {
      // Get all the possible uses from the boxOptions
      if (!this.boxOptions || !this.boxOptions.useSizeMapping) {
        return [];
      }
      return this.boxOptions.useSizeMapping.map(item => item.use);
    },
    boxSizes() {
      // Default sizes are whatever the first use has listed.
      const defaultSizes =
        (this.boxOptions.useSizeMapping &&
          this.boxOptions.useSizeMapping.length > 0 &&
          this.boxOptions.useSizeMapping[0].sizes) ||
        [];
      // If use is selected, then available sizes for that use, otherwise none
      return this.selectedOptions ? this.selectedOptions.sizes : defaultSizes;
    },
    selectedOptions() {
      if (this.use) {
        return this.boxOptions.useSizeMapping.find(
          item => item.use.value == this.use
        );
      }
      return undefined;
    },
    selectedUseDisplay() {
      return (this.selectedOptions && this.selectedOptions.use.display) || '';
    },
    selectedSizeDisplay() {
      const size =
        (this.selectedOptions &&
          this.selectedOptions.sizes.find(s => s.value === this.size)) ||
        undefined;
      return (size && size.display) || '';
    },
  },
  watch: {
    selectedCustomerId() {
      // When customer location changes, refetch the appropriate projects query
      if (this.hasMultipleLocations && this.selectedCustomerId) {
        this.$apollo.queries.projectsForLocation.refetch();
      } else if (!this.hasMultipleLocations) {
        this.$apollo.queries.projects.refetch();
      }
      // Clear current project selection when location changes
      this.selectedProjectId = '';
      this.projectName.inputValue = '';
      this.selectedLotId = '';
      this.lotNumber.inputValue = '';
    },
    project() {
      this.$v.project.$touch();
    },
    lots() {
      this.checkForSingleLot();
    },
    use() {
      let options = this.boxOptions.useSizeMapping.find(
        item => item.use.value == this.use
      );
      if (!options.sizes.some(s => s.value === this.boxSize.inputValue)) {
        if (options.sizes.length > 0) {
          this.boxSize.inputValue = options.sizes[0].value;
        }
      }
    },
    boxOptions() {
      this.applyLastSetSettings();
    },
    lastSetSettings() {
      this.applyLastSetSettings();
    },
  },
  methods: {
    hideProjectList() {
      this.projectName.editing = false;
    },
    projectSelected(project) {
      if (project) {
        this.projectName.inputValue = project.name;
        this.selectedProjectId = project.projectId;
        // if they switch the selected project, they need to choose a new lot related to the new project
        if (!this.checkForSingleLot()) {
          this.selectedLotId = '';
          this.lotNumber.inputValue = '';
          this.projectName.editing = false;
        }
      }
    },
    lotSelected(lot) {
      if (lot) {
        this.lotNumber.inputValue = lot.name;
        this.selectedLotId = lot.subProjectId;
        this.$apollo.queries.availablePOs.refetch();
        this.lotNumber.editing = false;
      }
    },
    checkForSingleLot() {
      if (this.lots && this.lots.length === 1 && this.lots[0]) {
        this.lotSelected(this.lots[0]);
        return true;
      }
      return false;
    },
    poSelected(po) {
      this.selectedPO = po.purchaseOrderNumber;
      this.purchaseOrder.inputValue = this.selectedPO;
    },
    intendedUseSelected(use) {
      this.intendedUse.inputValue = use;
      this.intendedUse.editing = false;
    },
    boxSizeSelected(size) {
      this.boxSize.inputValue = size;
      this.boxSize.editing = false;
    },
    loadLastSetSettings() {
      // An explicit network-only read: Apollo Client 1.x caches results, and a
      // cached response could predate the Set the user just placed.
      this.$apollo
        .query({
          query: LAST_SET_SETTINGS,
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          this.lastSetSettings = (data && data.lastSetSettings) || null;
        })
        .catch(err => {
          window.console.error('Error loading last set settings', err);
        });
    },
    applyLastSetSettings() {
      // Wait until both the saved settings and the box options have loaded;
      // whichever arrives last triggers this.
      if (
        this.lastSetSettingsApplied ||
        !this.lastSetSettings ||
        !this.boxOptions ||
        !this.boxOptions.useSizeMapping
      ) {
        return;
      }
      this.lastSetSettingsApplied = true;

      // Never overwrite a choice the user has already made.
      if (this.intendedUse.inputValue || this.boxSize.inputValue) {
        return;
      }

      let settings = this.lastSetSettings;
      let options = this.boxOptions.useSizeMapping.find(
        item => item.use.value === settings.intendedUse
      );
      if (!options) {
        return;
      }

      this.intendedUse.inputValue = settings.intendedUse;

      // Only apply the size when it is still offered for the use; otherwise
      // leave it blank so the use() watcher picks the first allowed size.
      if (
        settings.boxSize &&
        options.sizes.some(size => size.value === settings.boxSize)
      ) {
        this.boxSize.inputValue = settings.boxSize;
      }
    },
    submit() {
      if (
        this.$v.$invalid ||
        (this.$refs.serviceRequest.$v.instructions &&
          !this.$refs.serviceRequest.$v.instructions.required)
      ) {
        this.$v.$touch();
        this.$refs.serviceRequest.$v.$touch();

        this.$modal.show('alert', {
          title: 'Insufficient Info',
          message:
            'Some of the fields are invalid. Please check that you have entered the required information.',
          type: 'warning',
        });
        return;
      }

      this.createOrder();
    },
    createOrder() {
      this.$modal.show('wifi-loader', { message: 'Processing Request' });

      let instructions = this.$refs.serviceRequest.specialInstructions
        .inputValue;
      let date = this.$refs.serviceRequest.preferredBy.inputValue;

      let subProject = this.selectedLotId.length > 0 && this.selectedLotId;

      this.$apollo
        .mutate({
          mutation: CREATE_DISPATCH_SET,
          variables: {
            projectId: this.selectedProjectId,
            date,
            instructions,
            subProject,
            use: this.intendedUse.inputValue,
            size: this.boxSize.inputValue,
            ponumber: this.purchaseOrder.inputValue,
          },
        })
        .then(result => {
          let status = result.data.createDispatchSet;
          let success = status.code === 200;
          if (success) {
            this.$modal.hide('*');
            // Push an optimistic Set order to the list so it appears immediately,
            // before the SQS consumer writes to FileMaker.
            const optimisticOrder = {
              workOrderId: null,
              tripType: 'Set',
              status: 'SetCreated',
              intendedUse: this.intendedUse.inputValue,
              boxSize: this.boxSize.inputValue,
              box: null,
              project: {
                projectId: this.selectedProjectId,
                name: this.projectName.inputValue,
              },
              subProject: {
                name: this.lotNumber.inputValue,
              },
              dateNeededBy: this.$refs.serviceRequest.preferredBy.inputValue,
              _optimisticKey: `set-${Date.now()}`,
              _optimisticAt: Date.now(),
            };
            this.$store.commit('orders/addOptimisticOrder', optimisticOrder);
          }
          this.$broadcast().$emit('order-submitted', {
            type: 'set',
            success,
          });
          this.$modal.hide('wifi-loader');
          this.$modal.show('alert', {
            type: success ? 'success' : 'warning',
            title: status.title,
            subtitle: success ? 'Your order has been submitted.' : '',
            message: status.message,
            actions: [
              {
                title: 'Ok',
                classes: ['is-black'],
                callback: () => {
                  if (success) {
                    this.$modal.hide('*');
                  } else {
                    this.$modal.hide('alert');
                  }
                },
              },
            ],
          });
        })
        .catch(() => {
          this.$modal.hide('wifi-loader');
          this.$modal.show('alert', {
            type: 'warning',
            title: 'Something Went Wrong!',
            message: 'There was an error proccessing this request',
            actions: [
              {
                title: 'Ok',
                classes: ['is-black'],
                callback: () => {
                  this.$modal.hide('*');
                },
              },
            ],
          });
        });
    },
  },
  mounted() {
    // Refetch the appropriate projects query based on location setup
    if (this.hasMultipleLocations && this.selectedCustomerId) {
      this.$apollo.queries.projectsForLocation.refetch();
    } else {
      this.$apollo.queries.projects.refetch();
    }
    this.$apollo.queries.boxOptions.refetch();
    this.loadLastSetSettings();
  },
};
</script>

<style lang="stylus" scoped>

.modal-content {
  background-color: white;
  // border-radius: $radius;
  border-radius: 20px; // temporary! need to use line above once variables are accessible
}
</style>
